import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { NewsPage } from '@/features/publicsite/NewsPage';
import { CommunityPage } from '@/features/publicsite/catalog/CommunityPage';
import { ForumPage } from '@/features/publicsite/community/ForumPage';
import { TopicPage } from '@/features/publicsite/community/TopicPage';
import { QaSessionPage } from '@/features/publicsite/community/QaSessionPage';
import { AssociationPage } from '@/features/publicsite/community/AssociationPage';
import { FundingPage } from '@/features/publicsite/catalog/FundingPage';
import { ResearchPage } from '@/features/publicsite/catalog/ResearchPage';
import { ResearchOrganizationProfilePage } from '@/features/publicsite/catalog/ResearchOrganizationProfilePage';
import { TrainingInstitutionProfilePage } from '@/features/publicsite/catalog/TrainingInstitutionProfilePage';
import { TrainingInstitutionsPage } from '@/features/publicsite/catalog/TrainingInstitutionsPage';
import { ResearchOrganizationsPage } from '@/features/publicsite/catalog/ResearchOrganizationsPage';
import { ResearchProjectPage } from '@/features/publicsite/catalog/ResearchProjectPage';
import { ServicesPage } from '@/features/publicsite/catalog/ServicesPage';
import { ServiceProviderProfilePage } from '@/features/publicsite/catalog/ServiceProviderProfilePage';
import { ServiceEvaluationPage } from '@/features/publicsite/catalog/ServiceEvaluationPage';
import { SupportAgenciesPage } from '@/features/publicsite/catalog/SupportAgenciesPage';
import { TrainingsPage } from '@/features/publicsite/catalog/TrainingsPage';
import { AdvisorsPage } from '@/features/publicsite/registry/AdvisorsPage';
import { LibraryPage } from '@/features/publicsite/registry/LibraryPage';
import { SitemapPage } from '@/features/publicsite/registry/SitemapPage';
import { ContactPage } from '@/features/publicsite/ContactPage';
import { SearchPage } from '@/features/publicsite/SearchPage';
import { EventDetailPage } from '@/features/publicsite/events/EventDetailPage';
import { EventsPage } from '@/features/publicsite/EventsPage';
import { CatalogPage } from '@/features/publicsite/CatalogCards';
import { HomePage } from '@/features/publicsite/HomePage';
import { ContentPage } from '@/features/publicsite/ContentPage';
import {
  newsEndpoint,
  pageEndpoint,
  policyEndpoint,
} from '@/features/publicsite/content/api/publicContentApi';

// The one lazy page of the public zone: the map pulls in Leaflet, which no other public page needs.
const MapPage = lazy(() =>
  import('@/features/publicsite/MapPage').then((m) => ({ default: m.MapPage })),
);

// The public zone. Anonymous — no RequireSession — and framed by PublicShell rather than
// AppShell, so it carries the site header and footer instead of the cabinet sidebar.
//
// These answer at the site root, not under /app: the router carries no basename, and the
// cabinet spells /app into its own paths instead. Each of these has a matching entry in
// SpaForwardController so a cold load of the URL reaches the shell; the two must be kept in
// step, since a route registered only here 404s before React ever boots.
export const publicRoutes: RouteObject[] = [
  { path: '/stiri', element: <NewsPage /> },
  { path: '/stiri/:slug', element: <ContentPage endpoint={newsEndpoint} story /> },
  { path: '/pagini/:slug', element: <ContentPage endpoint={pageEndpoint} /> },
  { path: '/politici/:slug', element: <ContentPage endpoint={policyEndpoint} /> },
  { path: '/instruiri', element: <TrainingsPage /> },
  // Static before dynamic, as /cercetare/organizatii is: React Router would otherwise read
  // "institutii" as a slug. Both need a SpaForwardController entry to survive a cold load.
  { path: '/instruiri/institutii', element: <TrainingInstitutionsPage /> },
  { path: '/instruiri/institutii/:id', element: <TrainingInstitutionProfilePage /> },
  { path: '/finantare', element: <FundingPage /> },
  { path: '/agentii-sprijin', element: <SupportAgenciesPage /> },
  { path: '/cercetare', element: <ResearchPage /> },
  // Before the project route: a static segment outranks a dynamic one in React Router,
  // so /cercetare/organizatii reaches the list rather than being read as a project id.
  { path: '/cercetare/organizatii', element: <ResearchOrganizationsPage /> },
  { path: '/cercetare/organizatii/:id', element: <ResearchOrganizationProfilePage /> },
  { path: '/cercetare/:slug', element: <ResearchProjectPage /> },
  { path: '/servicii', element: <ServicesPage /> },
  { path: '/servicii/furnizori/:slug', element: <ServiceProviderProfilePage /> },
  { path: '/evaluare-livrare/:recordId', element: <ServiceEvaluationPage /> },
  { path: '/comunitate', element: <CommunityPage /> },
  { path: '/comunitate/forumuri/:id', element: <ForumPage /> },
  { path: '/comunitate/forumuri/:forumId/subiecte/:topicId', element: <TopicPage /> },
  { path: '/comunitate/qa/:id', element: <QaSessionPage /> },
  { path: '/comunitate/asociatii/:id', element: <AssociationPage /> },
  { path: '/consilieri', element: <AdvisorsPage /> },
  { path: '/biblioteca', element: <LibraryPage /> },
  { path: '/sitemap', element: <SitemapPage /> },
  { path: '/cautare', element: <SearchPage /> },
  { path: '/contact', element: <ContactPage /> },
  { path: '/evenimente', element: <EventsPage /> },
  { path: '/evenimente/:slug', element: <EventDetailPage /> },
  { path: '/', element: <HomePage /> },
  { path: '/catalog', element: <CatalogPage /> },
  { path: '/harta', element: <MapPage /> },
];
